import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import React, { useCallback, useEffect, useRef } from 'react';
import {
  Animated,
  FlatList,
  View,
  type ListRenderItemInfo,
  Dimensions,
} from 'react-native';
import InsuranceSearchDoing from '~/assets/img/InsuranceSearchDoing.png';
import InsuranceSearchDone from '~/assets/img/InsuranceSearchDone.png';
import InsuranceSearchStart from '~/assets/img/InsuranceSearchStart.png';
import Banner from '~/components/Banner';
import type { RootStackParamList } from '~/navigation/types';
import Page from './components/Page/index.web';
import type { WebListPage } from './types';
import type { InsuranceQuoteRequestDTO } from '~/dto/Insurance/insuranceQuoteRequestDTO';
import { insuranceQuoteRequestMapper } from '~/mappers/Insurance/insuranceQuoteRequestMapper';
import useMarisaAutoForm from '~/stores/marisaAutoForm';
import { useUserInfoStore } from '~/stores/userInfo';
import type { MarisaAutoStepsProps } from '../..';
import { doInsuranceQuoteUseCase } from '~/useCases/AssegurouUseCases/DoInsuranceQuoteUseCase';
import { DefaultScreenContainer } from '~/components';
import { ANONYMOUS_USER } from '~/storage/constants';
import { LocalStorage } from '~/storage/LocalStorage';
import type { AnonymousUserState } from '~/storage/types';

const getUserDocument = async (loggedInUserDocument?: string) => {
  if (loggedInUserDocument) return loggedInUserDocument;

  const anonymousUser = await LocalStorage.get<AnonymousUserState>(
    ANONYMOUS_USER
  );

  return anonymousUser!.document;
};

export const SUCCESS_PAGE_WIDTH =
  Dimensions.get('screen').width <= 1279 ? Dimensions.get('screen').width : 500;

const PAGES: WebListPage[] = [
  {
    id: 'start_simulation',
    image: InsuranceSearchStart,
    text: 'Estamos encontrando os melhores valores para você!',
  },
  {
    id: 'find_best_insurances',
    image: InsuranceSearchDoing,
    text: 'As melhores seguradoras para seu veículo e para proteger seu patrimônio!',
  },
  {
    id: 'done_simulation',
    image: InsuranceSearchDone,
    text: 'Tudo certo! Aproveite as simulações e oportunidades que separamos para você!',
  },
];

const LoadingSimulation = ({ onContinue, goToStep }: MarisaAutoStepsProps) => {
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>();

  const listRef = useRef<FlatList>(null);
  const interval = useRef<NodeJS.Timeout>();
  const index = useRef<number>();
  const listScroll = useRef(new Animated.Value(0)).current;

  const progress = listScroll.interpolate({
    inputRange: [0, SUCCESS_PAGE_WIDTH, SUCCESS_PAGE_WIDTH * 2],
    outputRange: ['20%', '50%', '100%'],
    extrapolate: 'clamp',
  });

  const renderItem = useCallback(
    ({ item }: ListRenderItemInfo<WebListPage>) => <Page {...item} />,
    []
  );

  useEffect(
    () =>
      navigation.addListener('beforeRemove', (e) => {
        if (e.data.action.type !== 'POP' && e.data.action.type !== 'GO_BACK') {
          return;
        }
        e.preventDefault();
      }),
    [navigation]
  );

  useEffect(() => {
    async function doInsuranceQuotes() {
      try {
        const userDocument = await getUserDocument(
          useUserInfoStore.getState().user?.document
        );

        const insuranceRequest: InsuranceQuoteRequestDTO =
          insuranceQuoteRequestMapper(
            useMarisaAutoForm.getState().form!,
            userDocument
          );

        const result = await doInsuranceQuoteUseCase(insuranceRequest);

        if (result.success && result.insuranceQuotes.length > 0) {
          if (result.insuranceQuotes.length > 0)
            useMarisaAutoForm
              .getState()
              .setInsuranceQuotes(result.insuranceQuotes);

          if (interval.current) {
            clearInterval(interval.current);
          }

          interval.current = setInterval(() => {
            if ((index.current || 0) === PAGES.length - 1) {
              clearInterval(interval.current);
              onContinue?.();
              return;
            }

            const newIndex = (index.current || 0) + 1;

            listRef.current?.scrollToIndex({
              index: newIndex,
              animated: true,
            });

            index.current = newIndex;
          }, 2000);
        } else {
          if (goToStep) {
            goToStep();
          }
        }
      } catch (err) {
        if (goToStep) {
          goToStep();
        }
      }
    }

    doInsuranceQuotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <DefaultScreenContainer>
      <View className="flex-1 items-center mx-auto xl:flex-row xl:pt-12">
        <Banner type="marisa-auto" />

        <View className={`w-[${SUCCESS_PAGE_WIDTH}px] h-[600px]`}>
          <Animated.FlatList
            style={{ width: SUCCESS_PAGE_WIDTH }}
            contentContainerStyle={{
              width: SUCCESS_PAGE_WIDTH,
            }}
            ref={listRef}
            horizontal
            data={PAGES}
            pagingEnabled
            scrollEnabled={false}
            renderItem={renderItem}
            keyExtractor={({ id }) => id}
            showsHorizontalScrollIndicator={false}
            onScroll={Animated.event(
              [
                {
                  nativeEvent: {
                    contentOffset: {
                      x: listScroll,
                    },
                  },
                },
              ],
              { useNativeDriver: false }
            )}
          />

          <View className="h-[50px] items-center">
            <View className="h-1 w-[300px] bg-secondary-400 rounded">
              <Animated.View
                className="h-[100%] bg-primary-600 rounded"
                style={{ width: progress }}
              />
            </View>
          </View>
        </View>
      </View>
    </DefaultScreenContainer>
  );
};

export default LoadingSimulation;
